import React, { useEffect } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { useObserver } from "mobx-react";
import {
  UserPermissions,
  useGetBookingAssignedToTotalLazyQuery,
  useGetBookingKitAssignedToTotalLazyQuery
} from "@graphql";
import { MainMenu as MainMenuComponent } from "@components/Menu/MainMenu";
import { MenuItem } from "@components/Menu";
import { store } from "@store";
import { items, MenuPathsEnum } from "./items";
import { environment } from "../../../environments/environment";
import { usePolling } from "@bonliva-crm/ui";

type Props = RouteComponentProps<any>;

export const filterItems = (
  menuItem: MenuItem,
  userPermission: UserPermissions,
  can: (permission: string) => boolean
) => {
  const isBusinessProd = environment.businessEnv === "production";

  switch (menuItem.path) {
    case "/consultant_import":
      return !isBusinessProd || userPermission.CanImportConsultants;
    case "/reports/bookings":
      return !isBusinessProd || userPermission.CanAccessReports;
    case "/config_editor":
      return !isBusinessProd || can("config-editor:read:all");
    case "/documents":
      return can("shared-documents:read:all");
    default:
      return true;
  }
};

function MainMenu(_props: Props) {
  const { user, ui, todoStore } = store();

  const [getBookingAssignedToTotal, { data: resBookings }] =
    useGetBookingAssignedToTotalLazyQuery({ fetchPolicy: "network-only" });
  const [getBookingKitAssignedToTotal, { data: resKits }] =
    useGetBookingKitAssignedToTotalLazyQuery({ fetchPolicy: "network-only" });

  useEffect(() => {
    getBookingAssignedToTotal();
    getBookingKitAssignedToTotal();
  }, []);

  usePolling(
    () => {
      getBookingAssignedToTotal();
      getBookingKitAssignedToTotal();
    },
    "*",
    { interval: 600000 }
  );

  const getMenuItems = (): MenuItem[] => {
    return items
      .filter((i) =>
        filterItems(i, user.myProfile.Permissions, (perm) => store().user.can(perm))
      )
      .map((item) => {
        let badgeContent = ui.mainMenuBadges[item.path];

        if (item.path === MenuPathsEnum.TIMEREPORT_KIT_FLOW) {
          badgeContent = resBookings?.getBookingAssignedToTotal?.total || null;
        }

        if (item.path === MenuPathsEnum.KITS) {
          badgeContent = resKits?.getBookingKitAssignedToTotal?.total || null;
        }

        if (item.path === MenuPathsEnum.DASHBOARD) {
          item.children = ui._userTicketTeams.map((x) => {
            return {
              env: "production",
              badgeContent: x.badge,
              label: x.teamRef.Name,
              data: x.teamRef.NodeID,
              active: todoStore.selectedTeam?.NodeID === x.teamRef.NodeID,
              favorite: user.myProfile.favoriteTeam === x.teamRef.Name,
              onClick: () => todoStore.setTeam(x.teamRef.NodeID),
              onFavorite: (favorite: string) => user.updateFavoriteTeam(favorite)
            };
          });
        }

        return {
          ...item,
          badgeContent: badgeContent || null,
          hiddenInProduction: item.env !== "production"
        };
      });
  };

  return useObserver(() => (
    <MainMenuComponent
      items={getMenuItems()}
      onItemClick={(item) => {
        item.onClick?.();
      }}
    />
  ));
}

export default withRouter(MainMenu);
